import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { type IStickyContext, StickyContext } from "./StickyContext";
import { type IStickyLayer } from "./StickyContext.type";
import {
  stickyLayerReducer,
  StickyLayerReplaceAction,
} from "./StickyLayer.reducer";

function isStickyLayerEqual(
  a: IStickyLayer | undefined,
  b: IStickyLayer | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.size === b.size && a.level === b.level;
}

function useHasStickyLayerChanged(value: IStickyLayer | undefined) {
  const previousValueRef = useRef(value);
  const previousValue = previousValueRef.current;
  const hasChanged = !isStickyLayerEqual(value, previousValue);
  previousValueRef.current = value;
  return [hasChanged, previousValue] as const;
}

interface IStickyProviderProps {
  initialTopLayer?: IStickyLayer;
  initialBottomLayer?: IStickyLayer;
  children: ReactNode;
}

export const StickyProvider: FunctionComponent<IStickyProviderProps> = ({
  children,
  initialTopLayer,
  initialBottomLayer,
}) => {
  const [reducerState, dispatchLayerChangeAction] = useReducer(
    stickyLayerReducer,
    {
      top: initialTopLayer ? [initialTopLayer] : [],
      bottom: initialBottomLayer ? [initialBottomLayer] : [],
    },
  );

  const providerValue: IStickyContext = useMemo(
    () => ({ ...reducerState, dispatchLayerChangeAction }),
    [reducerState],
  );

  const [hasInitialTopLayerChanged, oldInitialTopLayer] =
    useHasStickyLayerChanged(initialTopLayer);

  useEffect(() => {
    if (hasInitialTopLayerChanged) {
      dispatchLayerChangeAction(
        new StickyLayerReplaceAction(
          {
            oldLayer: oldInitialTopLayer,
            newLayer: initialTopLayer,
          },
          "top",
        ),
      );
    }
  }, [hasInitialTopLayerChanged, oldInitialTopLayer, initialTopLayer]);

  const [hasInitialBottomLayerChanged, oldInitialBottomLayer] =
    useHasStickyLayerChanged(initialBottomLayer);
  useEffect(() => {
    if (hasInitialBottomLayerChanged) {
      dispatchLayerChangeAction(
        new StickyLayerReplaceAction(
          {
            oldLayer: oldInitialBottomLayer,
            newLayer: initialBottomLayer,
          },
          "bottom",
        ),
      );
    }
  }, [hasInitialBottomLayerChanged, oldInitialBottomLayer, initialBottomLayer]);

  return (
    <StickyContext.Provider value={providerValue}>
      {children}
    </StickyContext.Provider>
  );
};
